import React from 'react'
import ProductCard from '@scentregroup/shared/components/product-card'
import {
  discountedPriceRange,
  priceRange,
  discountPercentageRange,
} from '@scentregroup/shared/helpers/price-ranges'
import SearchResult from '../search-result'
import style from '../index.module.scss'
import {
  Index,
  StoreHit,
  Segment,
  SegmentRender,
  Hit,
  RenderContext,
} from '../types'

const hrefForStore = (centreSlug: string, store: StoreHit): string =>
  `/${centreSlug}/store/${store.objectID}/${store.slug}`

const renderRetailer: SegmentRender<'retailer'> = ({ hit, onResultClick }) => (
  <SearchResult
    key={hit.objectID}
    href={`/store/${hit.objectID}/${hit.slug}`}
    imageUrl={hit.img}
    highlightField="title"
    hit={hit}
    description="Shop online"
    className={style.storeRow}
    onClick={() => onResultClick(hit)}
  />
)

const renderStore: SegmentRender<'store'> = ({ hit, onResultClick, context }) =>
  context.centre ? (
    <SearchResult
      key={hit.objectID}
      href={hrefForStore(context.centre.slug, hit)}
      imageUrl={hit.img}
      highlightField="titleWithOptionalStatus"
      hit={hit}
      description={context.centre.title}
      className={style.storeRow}
      onClick={() => onResultClick(hit)}
    />
  ) : null

type ServiceImgProps = Partial<Parameters<typeof SearchResult>[0]>

const serviceImgProps = (
  context: RenderContext,
  hit: Hit<'site' | 'service'>
): ServiceImgProps =>
  context.isEvolved ? { noImage: true } : { imageUrl: hit.img }

const renderSite: SegmentRender<'site'> = ({ hit, onResultClick, context }) =>
  context.centre ? (
    <SearchResult
      key={hit.objectID}
      href={(hit.href || '').replace('CENTRE', context.centre.slug)}
      highlightField="text"
      hit={hit}
      onClick={() => onResultClick(hit)}
      {...serviceImgProps(context, hit)}
    />
  ) : null

const renderService: SegmentRender<'service'> = ({
  hit,
  onResultClick,
  context,
}) =>
  context.centre ? (
    <SearchResult
      key={hit.objectID}
      href={`/${context.centre.slug}/service/${hit.objectID}/${hit.slug}`}
      highlightField="title"
      hit={hit}
      onClick={() => onResultClick(hit)}
      {...serviceImgProps(context, hit)}
    />
  ) : null

const renderOffer: SegmentRender<'offer'> = ({ hit, onResultClick, context }) =>
  context.centre ? (
    <SearchResult
      key={hit.objectID}
      href={`/${context.centre.slug}/offer/${hit.objectID}/${hit.slug}`}
      imageUrl={hit.img}
      highlightField="title"
      hit={hit}
      description={hit.storeTitles.join(', ')}
      onClick={() => onResultClick(hit)}
    />
  ) : null

const renderEvent: SegmentRender<'event'> = ({ hit, onResultClick, context }) =>
  context.centre ? (
    <SearchResult
      key={hit.objectID}
      href={`/${context.centre.slug}/event/${hit.objectID}/${hit.slug}`}
      imageUrl={hit.img}
      highlightField="title"
      hit={hit}
      description={hit.shortDescription}
      onClick={() => onResultClick(hit)}
    />
  ) : null

const renderNews: SegmentRender<'news'> = ({ hit, onResultClick, context }) =>
  context.centre ? (
    <SearchResult
      key={hit.objectID}
      href={`/${context.centre.slug}/news/${hit.objectID}/${hit.slug}`}
      imageUrl={hit.img}
      highlightField="title"
      hit={hit}
      description={hit.shortDescription}
      onClick={() => onResultClick(hit)}
    />
  ) : null

const renderLocalStory: SegmentRender<'story'> = ({
  hit,
  onResultClick,
  context,
}) =>
  context.centre ? (
    <SearchResult
      key={hit.objectID}
      href={`/${context.centre.slug}/story/${hit.objectID}/${hit.slug}`}
      imageUrl={hit.img}
      highlightField="title"
      hit={hit}
      description={hit.shortDescription}
      onClick={() => onResultClick(hit)}
    />
  ) : null

const renderNationalStory: SegmentRender<'story'> = ({
  hit,
  onResultClick,
}) => (
  <SearchResult
    key={hit.objectID}
    href={`/story/${hit.objectID}/${hit.slug}`}
    imageUrl={hit.img}
    highlightField="title"
    hit={hit}
    description={hit.shortDescription}
    onClick={() => onResultClick(hit)}
  />
)

const renderCentre: SegmentRender<'centre'> = ({ hit, onResultClick }) => (
  <SearchResult
    noImage
    key={hit.objectID}
    href={hit.href}
    highlightField="title"
    hit={hit}
    onClick={() => onResultClick(hit)}
  />
)

const renderOnlineService: SegmentRender<'online_service'> = ({
  hit,
  onResultClick,
}) => (
  <SearchResult
    noImage
    key={hit.objectID}
    href={hit.href}
    highlightField="title"
    hit={hit}
    onClick={() => onResultClick(hit)}
  />
)

const renderNearCentreStore: SegmentRender<'store'> = ({
  hit,
  onResultClick,
}) => (
  <SearchResult
    noImage
    key={hit.objectID}
    href={hrefForStore(hit.centreSlug, hit)}
    highlightField="title"
    hit={hit}
    description={hit.centreName}
    className={style.otherStoreRow}
    onClick={() => onResultClick(hit)}
  />
)

const renderProduct: SegmentRender<'products_au'> = ({
  hit,
  onResultClick: _ignore,
}) => (
  <ProductCard
    product={hit}
    shop={hit.shop}
    priceRange={priceRange(hit)}
    discountedPriceRange={discountedPriceRange(hit)}
    discountPercentageRange={discountPercentageRange(hit)}
  />
)

const renderCategories: SegmentRender<'categories_au'> = ({
  hit,
  onResultClick,
}) => (
  <SearchResult
    noImage
    key={hit.objectID}
    href={`/shop/products/${hit.objectID}`}
    highlightField="displayTitle"
    hit={hit}
    onClick={() => onResultClick(hit)}
  />
)

const segmentRenders: Record<Segment, SegmentRender<Index>> = {
  retailers: renderRetailer,
  stores: renderStore,
  sites: renderSite,
  services: renderService,
  offers: renderOffer,
  events: renderEvent,
  news: renderNews,
  localStories: renderLocalStory,
  nationalStories: renderNationalStory,
  centres: renderCentre,
  onlineServices: renderOnlineService,
  nearCentreStores: renderNearCentreStore,
  products: renderProduct,
  categories: renderCategories,
}

export default function getRenderForSegment(
  segment: Segment
): SegmentRender<Index> {
  return segmentRenders[segment]
}
